/**
 * Leaflet Marker Cluster Configuration
 * 
 * This file contains all settings for how marker clusters appear and behave.
 * Modify these values to experiment with different cluster appearances and behaviors.
 */

// ============================================================================
// CLUSTER APPEARANCE SETTINGS
// ============================================================================

/**
 * Color gradient configuration
 * Controls how cluster colors change from white to blue based on marker count
 */
export const CLUSTER_COLORS = {
  // Starting color (for low counts) - light orange/yellow (fire start)
  startColor: { r: 255, g: 240, b: 240 },
  
  // Ending color (for high counts) - deep red/orange (fire color)
  // Fire gradient: from orange-yellow to deep red-orange
  endColor: { r: 255, g: 99, b: 30 },
  
  // Maximum count to use for color calculation
  // Clusters with this many markers will reach the endColor
  maxCountForColor: 1000,
  
  // Color calculation method: 'linear' or 'logarithmic'
  // Logarithmic provides smoother transitions for large ranges
  colorScale: 'logarithmic', // 'linear' | 'logarithmic'
};

/**
 * Cluster icon size settings
 * Controls how large cluster icons appear based on marker count
 */
export const CLUSTER_SIZES = {
  // Size thresholds and corresponding icon sizes (in pixels)
  // Reduced by 1.5x: 30/1.5=20, 40/1.5=27, 50/1.5=33, 60/1.5=40
  thresholds: [
    { maxCount: 10, size: 30 },
    { maxCount: 100, size: 40 },
    { maxCount: 500, size: 50 },
    { maxCount: Infinity, size: 60 },
  ],
  
  // Minimum and maximum icon sizes (in pixels)
  // Reduced by 1.5x: 20/1.5=13, 70/1.5=47
  minSize: 13,
  maxSize: 70,
};

/**
 * Cluster icon styling
 * Controls visual appearance of cluster icons
 */
export const CLUSTER_STYLING = {
  // Transparency/Opacity settings
  // Value between 0 (fully transparent) and 1 (fully opaque)
  // 0.8 = 80% opacity (20% transparent)
  backgroundOpacity: 0.5,
  
  // Border settings
  borderWidth: 3,
  borderColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: '50%', // '50%' for circles, or '8px' for rounded squares
  
  // Text settings
  textColorLight: '#333333', // Text color for light backgrounds
  textColorDark: '#ffffff',  // Text color for dark backgrounds
  textColorThreshold: 0.5,   // Intensity threshold to switch text color
  
  // Font settings
  fontSizeSmall: '14px',  // For clusters < 100 markers
  fontSizeLarge: '16px',  // For clusters >= 100 markers
  fontWeight: 'bold',
  
  // Shadow settings
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
};

// ============================================================================
// CLUSTER BEHAVIOR SETTINGS
// ============================================================================

/**
 * Cluster grouping behavior
 * Controls when and how markers are grouped into clusters
 */
export const CLUSTER_BEHAVIOR = {
  // Maximum radius (in pixels) to search for nearby markers to cluster
  // Smaller values = tighter clusters, larger values = more spread out
  maxClusterRadius: 50,
  
  // Zoom level at which clustering is disabled
  // At this zoom and above, all markers show individually
  // Range: 0-18 (typical map zoom levels)
  disableClusteringAtZoom: 10,
  
  // When clicking a cluster at max zoom, spread markers in a spider pattern
  spiderfyOnMaxZoom: true,
  
  // Show the coverage area (circle) when hovering over a cluster
  showCoverageOnHover: true,
  
  // Automatically zoom to show all markers when clicking a cluster
  zoomToBoundsOnClick: true,
  
  // Animation duration for zoom operations (in milliseconds)
  zoomAnimationDuration: 200,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate color intensity based on marker count
 * Returns a value between 0 (start color) and 1 (end color)
 */
export const calculateColorIntensity = (count) => {
  const { maxCountForColor, colorScale } = CLUSTER_COLORS;
  
  if (colorScale === 'logarithmic') {
    // Logarithmic scale for smoother transitions
    return Math.min(1, Math.log10(count + 1) / Math.log10(maxCountForColor + 1));
  } else {
    // Linear scale
    return Math.min(1, count / maxCountForColor);
  }
};

/**
 * Calculate RGB color based on marker count
 * Returns an object with r, g, b values
 */
export const calculateClusterColor = (count) => {
  const intensity = calculateColorIntensity(count);
  const { startColor, endColor } = CLUSTER_COLORS;
  
  return {
    r: Math.floor(startColor.r - (startColor.r - endColor.r) * intensity),
    g: Math.floor(startColor.g - (startColor.g - endColor.g) * intensity),
    b: Math.floor(startColor.b - (startColor.b - endColor.b) * intensity),
  };
};

/**
 * Calculate cluster icon size based on marker count
 * Returns size in pixels
 */
export const calculateClusterSize = (count) => {
  const { thresholds, minSize, maxSize } = CLUSTER_SIZES;
  
  for (const threshold of thresholds) {
    if (count <= threshold.maxCount) {
      return Math.max(minSize, Math.min(maxSize, threshold.size));
    }
  }
  
  return maxSize;
};

/**
 * Determine text color based on background color intensity
 * Returns color hex string
 */
export const getTextColor = (intensity) => {
  const { textColorLight, textColorDark, textColorThreshold } = CLUSTER_STYLING;
  return intensity > textColorThreshold ? textColorDark : textColorLight;
};

/**
 * Create custom cluster icon
 * This function is used by Leaflet to render cluster icons
 */
export const createClusterIcon = (cluster, L) => {
  const count = cluster.getChildCount();
  
  // Calculate color
  const color = calculateClusterColor(count);
  
  // Apply opacity/transparency to background color
  const { backgroundOpacity } = CLUSTER_STYLING;
  const backgroundColor = `rgba(${color.r}, ${color.g}, ${color.b}, ${backgroundOpacity})`;
  
  // Calculate size
  const size = calculateClusterSize(count);
  
  // Determine text color
  const intensity = calculateColorIntensity(count);
  const textColor = getTextColor(intensity);
  
  // Get font size
  const fontSize = count < 100 
    ? CLUSTER_STYLING.fontSizeSmall 
    : CLUSTER_STYLING.fontSizeLarge;
  
  // Build style string
  const {
    borderWidth,
    borderColor,
    borderRadius,
    fontWeight,
    boxShadow,
  } = CLUSTER_STYLING;
  
  const style = `
    background-color: ${backgroundColor};
    width: ${size}px;
    height: ${size}px;
    border-radius: ${borderRadius};
    border: ${borderWidth}px solid ${borderColor};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${fontWeight};
    color: ${textColor};
    font-size: ${fontSize};
    box-shadow: ${boxShadow};
  `;
  
  return L.divIcon({
    html: `<div style="${style}">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size)
  });
};

/**
 * Create the default (non-selected) profile marker icon.
 * A small circle styled to match the cluster appearance for count=1.
 */
export const createDefaultProfileIcon = (L) => {
  const { startColor } = CLUSTER_COLORS;
  const { backgroundOpacity, borderWidth, borderColor, borderRadius, boxShadow } = CLUSTER_STYLING;
  const size = 20;
  const bg = `rgba(${startColor.r}, ${startColor.g}, ${startColor.b}, ${backgroundOpacity})`;
  const style = `
    background-color: ${bg};
    width: ${size}px;
    height: ${size}px;
    border-radius: ${borderRadius};
    border: ${borderWidth}px solid ${borderColor};
    box-shadow: ${boxShadow};
    box-sizing: border-box;
  `;
  return L.divIcon({
    html: `<div style="${style}"></div>`,
    className: 'custom-profile-marker-icon',
    iconSize: L.point(size, size),
    iconAnchor: L.point(size / 2, size / 2),
    popupAnchor: L.point(0, -size / 2),
  });
};

/**
 * Create the selected profile marker icon (default Leaflet blue pin).
 */
export const createSelectedProfileIcon = (L) => new L.Icon.Default();

/**
 * Get cluster group configuration object
 * This is the main export used by App.js
 */
export const getClusterGroupConfig = (L) => {
  return {
    maxClusterRadius: CLUSTER_BEHAVIOR.maxClusterRadius,
    spiderfyOnMaxZoom: CLUSTER_BEHAVIOR.spiderfyOnMaxZoom,
    showCoverageOnHover: CLUSTER_BEHAVIOR.showCoverageOnHover,
    zoomToBoundsOnClick: CLUSTER_BEHAVIOR.zoomToBoundsOnClick,
    disableClusteringAtZoom: CLUSTER_BEHAVIOR.disableClusteringAtZoom,
    iconCreateFunction: (cluster) => createClusterIcon(cluster, L),
  };
};

